import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/services/global.service';

@Component({
  selector: 'sd-releases-list',
  templateUrl: './releases-list.component.html',
  styleUrls: ['./releases-list.component.scss']
})
export class ReleasesListComponent implements OnInit {
  @Input() songs: any[] = [];
  @Input() totalCount: number = 0;
  @Input() moreLoading: boolean = false;

  @Output() onSelect = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<any>();
  @Output() onLoadMore = new EventEmitter<void>();

  // Filter state pill
  currentFilter: string = 'All';
  warningStates: boolean[] = [];

  constructor(
    public global: GlobalService,
    private router: Router
  ) { }

  ngOnInit() {
    console.log("song details", this.songs);
    this.warningStates = new Array(this.songs.length).fill(false);
  }

  hasIssue(song: any): boolean {
    return !!(song.trackIssue?.artworkIssue || song.trackIssue?.pdlIssue || song.trackIssue?.mIssue);
  }

  getDistributionStatus(song: any): string {
    if (this.hasIssue(song)) {
      return 'Action Required';
    }

    const status = song.distribution_status;
    if (!status || status.length === 0) return 'Drafts';

    if (status[0]?.status === 'Terminated') return 'Terminated';
    if (status[0]?.status === 'Hold By Songdew' && !status[0]?.on) return 'On hold';
    if (status[3]?.status === 'Live' && status[3]?.on) return 'Live';
    if (status[2]?.status === 'Scheduled' && status[2]?.on && !status[3]?.on) return 'Scheduled';
    if (status[1]?.status === 'In Process' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[1]?.status === 'Clarifications Required' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[1]?.status === 'Processed' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[0]?.status === 'Submitted' && status[0]?.on && !status[1]?.on) return 'Submitted';
    if (status[0]?.status === 'Drafts' && status[0]?.on && !status[1]?.on) return 'Drafts';

    return 'Unknown';
  }

  // For an album track, spotify_video is an array - [] when none exist, or
  // [{spotify_video_id, status, songid}, ...] where songid matches a pdlIssue track's
  // own mid. For a single, the backend sends the single entry unwrapped as a plain
  // object instead of a one-item array - normalized to an array here either way.
  private findSpotifyVideoEntry(song: any, track?: any): any {
    const raw = song?.spotify_video;
    const videos: any[] = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
    if (!videos.length) return null;
    if (track?.mid != null) {
      return videos.find((v: any) => v.songid === track.mid) || null;
    }
    return videos[0];
  }

  private hasSpotifyVideo(song: any, track?: any): boolean {
    return !!this.findSpotifyVideoEntry(song, track)?.spotify_video_id;
  }

  private static readonly VIDEO_STATUS_BY_CODE: { [code: number]: { label: string; cssClass: string } } = {
    0: { label: 'In Process', cssClass: 'badge-in-process' },
    1: { label: 'Live', cssClass: 'badge-live' },
    2: { label: 'Completed', cssClass: 'badge-live' },
    3: { label: 'Terminated', cssClass: 'badge-terminated' }
  };

  getVideoStatus(song: any): { label: string; cssClass: string } | null {
    if (this.getDistributionStatus(song) !== 'Live') return null;

    // No specific track here (this badge is one per card, not per track) - shows
    // whichever entry exists first as a stand-in for the release's video status.
    const entry = this.findSpotifyVideoEntry(song);
    if (!entry?.spotify_video_id) return null;

    // Paid but not yet submitted - no status code assigned yet, so no badge yet either.
    // Number('') is 0 in JS, so this has to be checked before Number(status) or it
    // silently reads as the real code 0 ("In Process").
    if (entry.status === null || entry.status === undefined || entry.status === '') {
      return null;
    }

    return ReleasesListComponent.VIDEO_STATUS_BY_CODE[Number(entry.status)]
      || { label: 'In Process', cssClass: 'badge-in-process' };
  }


  getDistributionStatusClass(song: any): string {
    const statusStr = this.getDistributionStatus(song);
    switch (statusStr) {
      case 'Live':
        return 'badge-live';
      case 'Action Required':
        return 'badge-action-required';
      case 'Terminated':
      case 'On hold':
        return 'badge-terminated';
      case 'Drafts':
        return 'badge-draft';
      case 'Submitted':
      case 'In Process':
      case 'Scheduled':
      default:
        return 'badge-in-process';
    }
  }

  // Filter logic getters
  get draftsCount() {
    return this.songs.filter(s => this.getDistributionStatus(s) === 'Drafts').length;
  }

  get inProcessCount() {
    return this.songs.filter(s => ['In Process', 'Submitted', 'Scheduled'].includes(this.getDistributionStatus(s)) && !this.hasIssue(s)).length;
  }

  get liveCount() {
    return this.songs.filter(s => this.getDistributionStatus(s) === 'Live').length;
  }

  get actionRequiredCount() {
    return this.songs.filter(s => this.hasIssue(s)).length;
  }

  get terminatedCount() {
    return this.songs.filter(s => this.getDistributionStatus(s) === 'Terminated').length;
  }

  get filteredSongs() {
    if (this.currentFilter === 'All') {
      return this.songs;
    }
    return this.songs.filter(song => {
      const status = this.getDistributionStatus(song);
      if (this.currentFilter === 'Drafts' || this.currentFilter === 'Draft') {
        return status === 'Drafts';
      }
      if (this.currentFilter === 'In Process') {
        return ['In Process', 'Submitted', 'Scheduled'].includes(status) && !this.hasIssue(song);
      }
      if (this.currentFilter === 'Live') {
        return status === 'Live';
      }
      if (this.currentFilter === 'Action Required') {
        return this.hasIssue(song);
      }
      if (this.currentFilter === 'Terminated') {
        return status === 'Terminated';
      }
      return true;
    });
  }

  setFilter(filter: string) {
    this.currentFilter = filter;
  }

  editSong(song: any) {
    this.global.gAEvents('my_release', 'edit', song.title, song.album_id > 0 ? 'album' : 'single');
    if (song.album_id == 0) {
      this.router.navigate(['distribute/music/song'], { queryParams: { slug: song.slug } });
    } else {
      this.router.navigate(['distribute/music/album'], { queryParams: { slug: song.slug } });
    }
  }

  // Only when audio is Live - clicking the artwork opens the public listen page
  // (SongDetailComponent, resolved dynamically by SongsUrlStructureComponent at /songs/:slug
  // or /albums/:slug for either case). Not Live: the artwork isn't clickable at all - stop the
  // click here so it doesn't fall through to the card's own (click)="selectSong(song)" either.
  onArtworkClick(song: any, event: Event) {
    event.stopPropagation();
    if (this.getDistributionStatus(song) !== 'Live') return;
    const path = song.album_id > 0 ? '/albums' : '/songs';
    this.router.navigate([path, song.slug]);
  }

  showDeleteWarning(index: number) {
    this.warningStates[index] = true;
  }

  hideDeleteWarning(index: number) {
    this.warningStates[index] = false;
  }

  confirmDelete(warningElement: any, song: any, index: number) {
    this.onDelete.emit({ element: warningElement, slug: song.slug, isAlbum: song.album_id > 0 });
    this.hideDeleteWarning(index);
  }

  selectSong(song: any) {
    this.onSelect.emit(song);
  }

  // The single action button's label - Drafts/Action Required get their own verb, every other
  // status (Live, In Process, Terminated, On hold, ...) just goes to the release detail view.
  getPrimaryActionLabel(song: any): string {
    const status = this.getDistributionStatus(song);
    if (status === 'Drafts') return 'Complete Release';
    if (status === 'Action Required') return 'Fix Issue';
    return 'View Details';
  }

  onPrimaryAction(song: any) {
    if (this.getDistributionStatus(song) === 'Drafts') {
      this.editSong(song);
    } else {
      this.selectSong(song);
    }
  }

  // Drives which items the per-card 3-dot menu shows - Delete only pre-submission, Promote (+
  // dummy Helpdesk) once there's something live/in-flight to promote, Helpdesk only once the
  // release is in a state (Action Required/Terminated/On hold) where promoting doesn't apply.
  getActionMenuMode(song: any): 'delete' | 'promote' | 'helpdesk' {
    const status = this.getDistributionStatus(song);
    if (status === 'Drafts') return 'delete';
    if (['Live', 'In Process', 'Submitted', 'Scheduled'].includes(status)) return 'promote';
    return 'helpdesk';
  }

  loadMore() {
    this.onLoadMore.emit();
  }
}
